import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Footer from '@/components/common/Footer';

const LandingPage = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/auth');
  };

  const features = [
    {
      title: "Event Management",
      description: "Create, organize, and manage events with ease. From small meetups to large conferences.",
      icon: "🎯"
    },
    {
      title: "Ticket Sales",
      description: "Sell tickets directly through our platform with secure payment processing.",
      icon: "🎫"
    },
    {
      title: "Attendee Management",
      description: "Track registrations, manage attendee lists, and communicate with participants.",
      icon: "👥"
    },
    {
      title: "Organization Tools",
      description: "Manage your organization, members, and finances all in one place.",
      icon: "🏢"
    },
    {
      title: "QR Code Validation",
      description: "Instant ticket validation with QR codes and real-time attendee tracking at events.",
      icon: "📱"
    },
    {
      title: "Bursary System",
      description: "Offer financial assistance to attendees through our integrated bursary system.",
      icon: "💰"
    }
  ];

  const gradientStyle = {
    background: "linear-gradient(45deg, #f804b4 0%, #08ccc4 100%)"
  };

  return (
    <HelmetProvider>
      <Helmet>
        <title>TriEvents - Event Management Made Simple</title>
        <meta name="description" content="Event ticketing has never been easier. Post your event for free and sell at competitive per ticket fees." />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://trievents.ca/" />
        <meta property="og:title" content="TriEvents - Event Management Made Simple" />
        <meta property="og:description" content="Event ticketing has never been easier. Post your event for free and sell at competitive per ticket fees." />
        <meta property="og:image" content="/branding/TriEvents_Logo.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://trievents.ca/" />
        <meta property="twitter:title" content="TriEvents - Event Management Made Simple" />
        <meta property="twitter:description" content="Event ticketing has never been easier. Post your event for free and sell at competitive per ticket fees." />
        <meta property="twitter:image" content="/branding/TriEvents_Logo.png" />
      </Helmet>
      <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-screen flex items-center" style={gradientStyle}>
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4 py-20 lg:py-32 w-full">
          <div className="text-center text-white">
            <div className="mb-12">
              <div className="inline-block bg-white rounded-lg p-8 shadow-2xl relative">
                {/* Ticket perforations on left */}
                <div className="absolute left-1 top-0 bottom-0 w-3 flex flex-col justify-center">
                  <div className="space-y-2">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="w-3 h-3 bg-gradient-to-r from-transparent via-gray-400 to-transparent rounded-full"></div>
                    ))}
                  </div>
                </div>
                
                {/* Ticket perforations on right */}
                <div className="absolute right-1 top-0 bottom-0 w-3 flex flex-col justify-center">
                  <div className="space-y-2">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="w-3 h-3 bg-gradient-to-l from-transparent via-gray-400 to-transparent rounded-full"></div>
                    ))}
                  </div>
                </div>
                
                <img 
                  src="/branding/TriEvents_Loading_Big_Text.gif" 
                  alt="TriEvents Logo" 
                  className="w-64 md:w-80 h-auto"
                />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
              Event Management Made Simple
            </h1>
            <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto opacity-90 leading-relaxed">
              The complete platform for organizations to create, manage, and host successful events. 
              From ticketing to attendee management, we've got you covered.
            </p>
            <div className="flex justify-center">
              <Button 
                onClick={handleGetStarted}
                size="lg"
                className="text-lg px-16 py-4 transform hover:scale-105 transition-transform duration-200"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Everything You Need to Host Amazing Events
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Our comprehensive platform provides all the tools your organization needs to create 
              successful events from start to finish.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                <CardHeader>
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              How It Works
            </h2>
            <p className="text-xl text-muted-foreground">
              Get started in just a few simple steps
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Create Your Organization</h3>
              <p className="text-muted-foreground">
                Set up your organization profile and invite team members to collaborate.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Plan Your Event</h3>
              <p className="text-muted-foreground">
                Create events, set up ticketing, and configure all the details.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-2">Host & Manage</h3>
              <p className="text-muted-foreground">
                Sell tickets, manage attendees, and host your successful event.
              </p>
            </div>
          </div>
        </div>
      </section>
      <Footer />
      </div>
    </HelmetProvider>
  );
};

export default LandingPage;
